/**
 * FAQ Enhanced Search & Navigation
 * Provides real-time search filtering and smooth category navigation
 */

class FaqEnhanced {
  constructor() {
    this.searchInput = document.querySelector('[data-faq-search]');
    this.faqItems = document.querySelectorAll('[data-faq-item]');
    this.categories = document.querySelectorAll('[data-faq-category]');
    this.categoryLinks = document.querySelectorAll('[data-category-link]');
    this.noResults = document.querySelector('[data-faq-no-results]');
    this.faqContainer = document.querySelector('.faq--enhanced');

    if (!this.searchInput) return;

    this.init();
  }

  init() {
    // Search functionality
    this.searchInput.addEventListener('input', this.handleSearch.bind(this));

    // Category navigation with scroll spy
    this.setupCategoryNavigation();

    // Intersection Observer for active category highlighting
    this.setupScrollSpy();

    console.log('FAQ Enhanced initialized');
  }

  /**
   * Handle search input and filter FAQ items
   */
  handleSearch(event) {
    const query = event.target.value.toLowerCase().trim();
    let visibleCount = 0;

    // If search is empty, show all items
    if (query === '') {
      this.showAllItems();
      return;
    }

    // Filter items based on search query
    this.faqItems.forEach(item => {
      const question = item.dataset.question.toLowerCase();
      const answer = item.dataset.answer.toLowerCase();
      const matches = question.includes(query) || answer.includes(query);

      if (matches) {
        item.classList.remove('faq__item--hidden');
        item.style.display = '';
        visibleCount++;
      } else {
        item.classList.add('faq__item--hidden');
        item.style.display = 'none';
      }
    });

    // Update categories visibility
    this.updateCategoriesVisibility();

    // Show/hide no results message
    if (visibleCount === 0) {
      this.noResults.style.display = 'block';
    } else {
      this.noResults.style.display = 'none';
    }
  }

  /**
   * Show all FAQ items (reset filter)
   */
  showAllItems() {
    this.faqItems.forEach(item => {
      item.classList.remove('faq__item--hidden');
      item.style.display = '';
    });

    this.categories.forEach(category => {
      const wrapper = category.closest('[data-faq-category]');
      if (wrapper) {
        wrapper.classList.remove('faq__category--hidden');
        wrapper.style.display = '';
      }
    });

    this.noResults.style.display = 'none';
  }

  /**
   * Update categories visibility based on visible items
   */
  updateCategoriesVisibility() {
    this.categories.forEach(category => {
      const wrapper = category.closest('[data-faq-category]');
      if (!wrapper) return;

      // Find next items until next category
      let nextElement = wrapper.nextElementSibling;
      let hasVisibleItems = false;

      while (nextElement && !nextElement.hasAttribute('data-faq-category')) {
        if (nextElement.hasAttribute('data-faq-item') &&
            !nextElement.classList.contains('faq__item--hidden')) {
          hasVisibleItems = true;
          break;
        }
        nextElement = nextElement.nextElementSibling;
      }

      // Show/hide category based on visible items
      if (hasVisibleItems) {
        wrapper.classList.remove('faq__category--hidden');
        wrapper.style.display = '';
      } else {
        wrapper.classList.add('faq__category--hidden');
        wrapper.style.display = 'none';
      }
    });
  }

  /**
   * Setup smooth scroll for category navigation
   */
  setupCategoryNavigation() {
    this.categoryLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href');
        const targetElement = document.querySelector(targetId);

        if (targetElement) {
          // Update active state
          this.updateActiveCategory(link);

          // Smooth scroll to target
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  /**
   * Update active category in navigation
   */
  updateActiveCategory(activeLink) {
    this.categoryLinks.forEach(link => {
      link.classList.remove('is-active');
    });
    activeLink.classList.add('is-active');
  }

  /**
   * Setup scroll spy for automatic active category highlighting
   */
  setupScrollSpy() {
    const options = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          const correspondingLink = document.querySelector(`[data-category-link][href="#${id}"]`);

          if (correspondingLink) {
            this.updateActiveCategory(correspondingLink);
          }
        }
      });
    }, options);

    // Observe all categories
    this.categories.forEach(category => {
      const categoryElement = category.closest('[data-faq-category]');
      if (categoryElement) {
        const categoryId = category.id;
        if (categoryId) {
          observer.observe(categoryElement);
        }
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FaqEnhanced();
  });
} else {
  new FaqEnhanced();
}
